import { FirebaseError } from 'firebase/app';
import type { Unsubscribe } from 'firebase/firestore';

import type {
  LocationAlert,
  LocationAlertPathRef,
} from '@src/models/interfaces/locationAlert.interface';
import { LocationAlertMapper } from '@src/models/mappers/LocationAlertMapper';
import { LocationAlertRepository } from '@src/repositories/LocationAlertRepository';

export const LOCATION_ALERT_ALREADY_EXISTS = 'LOCATION_ALERT_ALREADY_EXISTS';

export class LocationAlertService {
  private repository: LocationAlertRepository;

  constructor(repository: LocationAlertRepository = new LocationAlertRepository()) {
    this.repository = repository;
  }

  subscribeByDivisions(
    orgId: string,
    divIds: string[],
    onData: (alerts: LocationAlert[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    if (!orgId) {
      onData([]);
      return () => undefined;
    }

    return this.repository.subscribeByDivisions(
      orgId,
      divIds,
      dtos => {
        onData(dtos.map(LocationAlertMapper.fromDTO));
      },
      onError,
    );
  }

  subscribeOne(
    orgId: string,
    divId: string,
    locationId: string,
    onData: (alerts: LocationAlert[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    if (!orgId || !divId || !locationId) {
      onData([]);
      return () => undefined;
    }

    return this.repository.subscribeOne(
      orgId,
      divId,
      locationId,
      dtos => {
        onData(dtos.map(LocationAlertMapper.fromDTO));
      },
      onError,
    );
  }

  async reset(orgId: string, divId: string, locationAlertId: string): Promise<void> {
    if (!orgId || !divId || !locationAlertId) {
      throw new Error('Organization ID, division ID and location alert ID are required');
    }

    const dto = LocationAlertMapper.toResetDTO();
    await this.repository.update(orgId, divId, locationAlertId, dto);
  }

  async listLocationKeys(orgId: string, divIds: string[]): Promise<LocationAlertPathRef[]> {
    if (!orgId || divIds.length === 0) {
      return [];
    }

    return this.repository.listLocationKeys(orgId, divIds);
  }

  async createForLocation(orgId: string, divId: string, locationId: string): Promise<void> {
    if (!orgId || !divId || !locationId) {
      throw new Error('Organization ID, division ID and location ID are required');
    }

    const alreadyExists = await this.repository.exists(orgId, divId, locationId);
    if (alreadyExists) {
      throw new Error(LOCATION_ALERT_ALREADY_EXISTS);
    }

    await this.repository.createWithQuantity(
      orgId,
      divId,
      locationId,
      LocationAlertMapper.toCreateDTO({ orgId, divId, locationId, quantity: 0 }),
    );
  }

  async delete(orgId: string, divId: string, locationAlertId: string): Promise<void> {
    if (!orgId || !divId || !locationAlertId) {
      throw new Error('Organization ID, division ID and location alert ID are required');
    }

    await this.repository.delete(orgId, divId, locationAlertId);
  }

  /**
   * Moves an alert to another division/location path, preserving quantity and descriptions.
   * No-op when the path does not change.
   */
  async reassign(
    orgId: string,
    from: LocationAlertPathRef,
    to: LocationAlertPathRef,
    alert: Pick<LocationAlert, 'quantity' | 'descriptions'>,
  ): Promise<void> {
    if (!orgId || !from.divId || !from.locationId || !to.divId || !to.locationId) {
      throw new Error('Organization ID, division ID and location ID are required');
    }

    if (from.divId === to.divId && from.locationId === to.locationId) {
      return;
    }

    const targetExists = await this.repository.exists(orgId, to.divId, to.locationId);
    if (targetExists) {
      throw new Error(LOCATION_ALERT_ALREADY_EXISTS);
    }

    await this.repository.reassign(
      orgId,
      from,
      to,
      LocationAlertMapper.toCreateDTO({
        orgId,
        divId: to.divId,
        locationId: to.locationId,
        quantity: alert.quantity,
        descriptions: alert.descriptions,
      }),
    );
  }

  async registerVisit(orgId: string, divId: string, locationId: string): Promise<void> {
    if (!orgId || !divId || !locationId) {
      throw new Error('Organization ID, division ID and location ID are required');
    }

    try {
      await this.repository.incrementQuantity(orgId, divId, locationId);
    } catch (error) {
      if (error instanceof FirebaseError && error.code === 'not-found') {
        await this.repository.createWithQuantity(
          orgId,
          divId,
          locationId,
          LocationAlertMapper.toCreateDTO({ orgId, divId, locationId, quantity: 1 }),
        );
        return;
      }
      throw error;
    }
  }

  async submitDescription(
    orgId: string,
    divId: string,
    locationId: string,
    description: string,
  ): Promise<void> {
    if (!orgId || !divId || !locationId) {
      throw new Error('Organization ID, division ID and location ID are required');
    }

    const trimmed = description.trim();
    if (!trimmed) {
      return;
    }

    await this.repository.appendDescription(orgId, divId, locationId, {
      description: trimmed,
    });
  }
}

export default LocationAlertService;
